'use client';

import type {InputHTMLAttributes, ReactNode, TextareaHTMLAttributes} from 'react';
import {AlertCircle} from 'lucide-react';

const INPUT_CLASS =
  'w-full border border-iq-grey-light rounded-lg px-3 py-2.5 text-base text-iq-black placeholder:text-iq-grey-medium focus:outline-none focus:border-iq-grey-medium transition-colors';

export function FlowStepHeading({
  step,
  totalSteps,
  children,
}: {
  step: number;
  totalSteps: number;
  children: ReactNode;
}) {
  return (
    <div>
      <p className='text-xs font-semibold text-iq-grey-medium tracking-widest uppercase mb-4'>
        Question {step + 1} of {totalSteps}
      </p>
      <h2 className='text-2xl font-bold text-iq-black leading-snug'>{children}</h2>
    </div>
  );
}

export function FlowLabel({children}: {children: ReactNode}) {
  return <label className='text-base font-semibold text-iq-black'>{children}</label>;
}

export function FlowInput(props: Omit<InputHTMLAttributes<HTMLInputElement>, 'className'>) {
  return <input {...props} className={INPUT_CLASS} />;
}

export function FlowTextarea(
  props: Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'>,
) {
  return (
    <textarea
      {...props}
      className={`${INPUT_CLASS} resize-none leading-relaxed disabled:text-gray-500`}
    />
  );
}

export function FlowPillButton({
  selected,
  selectedClassName,
  onClick,
  children,
}: {
  selected: boolean;
  selectedClassName: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-base border transition-colors ${
        selected
          ? selectedClassName
          : 'bg-white text-iq-grey-dark border-iq-grey-light hover:border-iq-grey-medium'
      }`}>
      {children}
    </button>
  );
}

export function FlowValidationError({message}: {message: string}) {
  return (
    <div className='flex gap-2.5 rounded-lg border border-iq-red bg-red-50 p-3'>
      <AlertCircle className='w-4 h-4 text-iq-red shrink-0 mt-0.5' />
      <p className='text-base text-iq-grey-dark leading-relaxed'>{message}</p>
    </div>
  );
}
